<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import i18next from "i18next";
import { useThemeStore } from "@/stores/useThemeStore";
import { useTradingViewTheme } from "@/shared/composables/useTradingViewTheme";
import { loadOptimizedScript, cleanupScriptResources } from "@/shared/utils/optimizedScriptLoader";

const themeStore = useThemeStore();
const { tradingViewTheme, applyStylesToContainer } = useTradingViewTheme();
const container = ref<HTMLDivElement | null>(null);
let detachI18n: (() => void) | null = null;

const symbols = [
	{ proName: "NASDAQ:NVDA", title: "NVIDIA" },
	{ proName: "NASDAQ:AMZN", title: "Amazon" },
	{ proName: "NASDAQ:PYPL", title: "PayPal" },
	{ proName: "BMFBOVESPA:MGLU3", title: "Magazine Luiza" },
	{ proName: "NASDAQ:AAPL", title: "Apple" },
	{ proName: "COINBASE:SOLUSD", title: "Solana" },
	{ proName: "BITSTAMP:XRPUSD", title: "ZRP" },
	{ proName: "CRYPTOCAP:BNB", title: "BNB" },
	{ proName: "CRYPTO:BTCUSD", title: "Bitcoin" },
	{ proName: "BITSTAMP:ETHUSD", title: "Ethereum" },
	{ proName: "CRYPTOCAP:USDT.D", title: "USDT" },
	{ proName: "NASDAQ:MSFT", title: "Microsoft" },
	{ proName: "NASDAQ:META", title: "META" },
	{ proName: "COINBASE:AVAXUSD", title: "AVALANCHE" },
];

function mountWidget() {
	if (!container.value) return;

	cleanupScriptResources(container.value);

	const widgetDiv = document.createElement("div");
	widgetDiv.className = "tradingview-widget-container__widget";
	container.value.appendChild(widgetDiv);

	loadOptimizedScript({
		src: "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js",
		container: widgetDiv,
		content: {
			symbols,
			colorTheme: tradingViewTheme.value,
			locale: i18next.language || "en",
			largeChartUrl: "",
			isTransparent: false,
			showSymbolLogo: true,
		},
		loadingMessage: "Loading ticker...",
		errorMessage: "Ticker temporarily unavailable",
		priority: "low", // Ticker tape is decorative, lower priority
		timeout: 3000,
	});

	// Apply custom styles to match site theme
	if (container.value) {
		applyStylesToContainer(container.value);
	}
}

onMounted(() => {
	mountWidget();

	watch(
		() => themeStore.isDark,
		() => {
			mountWidget();
		},
	);

	const onLang = () => mountWidget();
	i18next.on("languageChanged", onLang);
	detachI18n = () => i18next.off("languageChanged", onLang);
});

onBeforeUnmount(() => {
	if (detachI18n) detachI18n();
	if (container.value) cleanupScriptResources(container.value);
});
</script>

<template>
    <div ref="container" class="tradingview-widget-container" />
</template>
